<template>
  <div
    ref="visualizations"
    class="visualizations-tabs-container"
  >
    <b-modal
      id="image-failed"
      size="lg"
      centered
      no-header
      no-footer
      no-close-on-backdrop
      no-close-on-esc
    >
      <h3>Error occurred while processing image!</h3>
      <div v-text="imageError" />
      <b-button
        class="ms-1 float-end"
        variant="primary"
        @click="$bvModal.hide('image-failed')"
      >
        OK
      </b-button>
    </b-modal>
    <b-card
      no-body
      class="visualizations-tabs-card pb-2"
    >
      <!-- `unmount-lazy` on every outer <b-tab> (below) is load-bearing: each
           tab holds its own inner column-selector <b-tabs> and they all share
           the single `v-model:index="activeColumn"`. bootstrap-vue-next keeps
           a pane mounted after its first show (unmountLazy defaults false),
           so without this, once a second chart type is visited several inner
           selectors coexist bound to the same index ref and their watchers
           fight over it (observed as a pane rendering with no active column
           that can't be reselected). Unmounting inactive panes keeps exactly
           one selector alive at a time - and remounting with a preserved
           index is only safe because each inner <b-tab> carries an explicit
           :id, see columnTabId(). -->
      <b-tabs
        v-model:index="activeTab"
        card
        lazy
        class="visualizations-tabs"
      >
          <b-tab
            title="Table"
            unmount-lazy
          >
            <GroupVisualizationHeader
              v-bind="headerBindings"
              @title-changed="groupTitleChanged(group, $event)"
              @column-clicked="columnClicked"
              @change-column-order="$emit('changeColumnOrder', group)"
            />
            <b-tabs
              v-model:index="activeColumn"
              pills
              lazy
              align="end"
              class="visualization-tabs"
            >
              <b-tab
                v-for="([columnName, column]) in allColumns()"
                :id="columnTabId('table', columnName)"
                :key="columnName"
                :title="column.label"
              >
                <b-form-group
                  v-if="correlationValues[columnName].length > 1"
                  :label="`Correlated with ${correlations[columnName].key}`"
                  label-cols="auto"
                >
                  <b-form-select
                    v-model="correlationValue[columnName]"
                    :options="correlationValues[columnName]"
                  />
                </b-form-group>
                <FileColumnMostCommonValuesTable
                  :label="column.label"
                  :type="column.type"
                  :most-common-values-checked="mostCommonValuesChecked[columnName]"
                  :most-common-values="allMostCommonValuesToShowBy(columnName)"
                  :ranges="group.data[columnName].ranges"
                  :value-tags="group.data[columnName].valueTags"
                  @checked-most-common-values-changed="(checked, value) => checkedMostCommonValuesChanged(columnName)(checked, value)"
                />
              </b-tab>
            </b-tabs>
          </b-tab>
          <b-tab
            v-if="nonKeyColumns().length > 0"
            title="Bar Chart"
            unmount-lazy
          >
            <GroupVisualizationHeader
              v-bind="headerBindings"
              @title-changed="groupTitleChanged(group, $event)"
              @column-clicked="columnClicked"
              @change-column-order="$emit('changeColumnOrder', group)"
            />
            <FileColumnMostCommonValuesGroupBarChart
              v-if="group.features.multiFeatureBarChart"
              :group="filteredMostCommonValuesToShow()"
            />
            <b-tabs
              v-else
              v-model:index="activeColumn"
              pills
              lazy
              align="end"
              class="visualization-tabs"
            >
              <b-tab
                v-for="([columnName, column]) in allColumns()"
                :id="columnTabId('bar', columnName)"
                :key="columnName"
                :title="column.label"
                :disabled="!nonKeyColumns().includes(columnName)"
              >
                <b-form-group
                  v-if="correlationValues[columnName].length > 1"
                  :label="`Correlated with ${correlations[columnName].key}`"
                  label-cols="auto"
                >
                  <b-form-select
                    v-model="correlationValue[columnName]"
                    :options="correlationValues[columnName]"
                  />
                </b-form-group>
                <FileColumnMostCommonValuesBarChart
                  :type="column.type"
                  :most-common-values="mostCommonValuesToShowBy(columnName)"
                  :ranges="group.data[columnName].ranges"
                />
              </b-tab>
            </b-tabs>
          </b-tab>
          <b-tab
            v-if="nonKeyColumns().length > 0"
            title="Pie Chart"
            unmount-lazy
          >
            <GroupVisualizationHeader
              v-bind="headerBindings"
              @title-changed="groupTitleChanged(group, $event)"
              @column-clicked="columnClicked"
              @change-column-order="$emit('changeColumnOrder', group)"
            />
            <b-tabs
              v-model:index="activeColumn"
              pills
              lazy
              align="end"
              class="visualization-tabs"
            >
              <b-tab
                v-for="([columnName, column]) in allColumns()"
                :id="columnTabId('pie', columnName)"
                :key="columnName"
                :title="column.label"
                :disabled="!nonKeyColumns().includes(columnName)"
              >
                <b-form-group
                  v-if="correlationValues[columnName].length > 1"
                  :label="`Correlated with ${correlations[columnName].key}`"
                  label-cols="auto"
                >
                  <b-form-select
                    v-model="correlationValue[columnName]"
                    :options="correlationValues[columnName]"
                  />
                </b-form-group>
                <FileColumnMostCommonValuesPieChart
                  :label="column.label"
                  :type="column.type"
                  :total-count="column.numValues"
                  :most-common-values="allMostCommonValuesToShowBy(columnName)"
                  :ranges="group.data[columnName].ranges"
                />
              </b-tab>
            </b-tabs>
          </b-tab>
          <!-- <b-tab
            v-if="group.features.multiFeatureStackedAreaChart"
            title="Area Chart"
          >
            <div
              v-for="([columnName,]) in allColumns()"
              :key="columnName"
              class="px-5"
            >
              <b-form-group
                v-if="correlationValues[columnName].length > 1"
                :label="`Correlated with ${correlations[columnName].key}`"
                label-cols="auto"
              >
                <b-form-select
                  v-model="correlationValue[columnName]"
                  :options="correlationValues[columnName]"
                />
              </b-form-group>
            </div>
            <FileColumnMostCommonValuesGroupAreaChart
              :group="mostCommonValuesToShow()"
            />
          </b-tab> -->
          <b-tab
            v-if="mapColumns().length > 0"
            title="Map"
            unmount-lazy
          >
            <GroupVisualizationHeader
              v-bind="headerBindings"
              @title-changed="groupTitleChanged(group, $event)"
              @column-clicked="columnClicked"
              @change-column-order="$emit('changeColumnOrder', group)"
            />
            <b-tabs
              v-model:index="activeColumn"
              pills
              lazy
              align="end"
              class="visualization-tabs"
            >
              <b-tab
                v-for="([columnName, column]) in allColumns()"
                :id="columnTabId('map', columnName)"
                :key="columnName"
                :title="column.label"
                :disabled="!mapColumns().includes(columnName)"
              >
                <b-form-group
                  v-if="correlationValues[columnName].length > 1"
                  :label="`Correlated with ${correlations[columnName].key}`"
                  label-cols="auto"
                >
                  <b-form-select
                    v-model="correlationValue[columnName]"
                    :options="correlationValues[columnName]"
                  />
                </b-form-group>
                <FileColumnMostCommonValuesIpMap
                  v-if="column.type === 'ip'"
                  :most-common-values="allMostCommonValuesToShowBy(columnName)"
                />
                <FileColumnMostCommonValuesCountryMap
                  v-else-if="column.type === 'country'"
                  :most-common-values="allMostCommonValuesToShowBy(columnName)"
                  :label="column.label"
                />
              </b-tab>
            </b-tabs>
          </b-tab>
          <b-tab
            v-if="hilbertMapColumns().length > 0"
            title="Hilbert Map"
            unmount-lazy
          >
            <GroupVisualizationHeader
              v-bind="headerBindings"
              @title-changed="groupTitleChanged(group, $event)"
              @column-clicked="columnClicked"
              @change-column-order="$emit('changeColumnOrder', group)"
            />
            <b-tabs
              v-model:index="activeColumn"
              pills
              lazy
              align="end"
              class="visualization-tabs"
            >
              <b-tab
                v-for="([columnName, column]) in allColumns()"
                :id="columnTabId('hilbert', columnName)"
                :key="columnName"
                :title="column.label"
                :disabled="!hilbertMapColumns().includes(columnName)"
              >
                <b-form-group
                  v-if="correlationValues[columnName].length > 1"
                  :label="`Correlated with ${correlations[columnName].key}`"
                  label-cols="auto"
                >
                  <b-form-select
                    v-model="correlationValue[columnName]"
                    :options="correlationValues[columnName]"
                  />
                </b-form-group>
                <FileColumnMostCommonValuesHilbert :column-name="columnName" />
              </b-tab>
            </b-tabs>
          </b-tab>
          <b-tab
            v-if="hasCorrelations"
            title="Correlation Table"
            unmount-lazy
          >
            <GroupVisualizationHeader
              v-bind="headerBindings"
              @title-changed="groupTitleChanged(group, $event)"
              @column-clicked="columnClicked"
              @change-column-order="$emit('changeColumnOrder', group)"
            />
            <b-tabs
              v-model:index="activeColumn"
              pills
              lazy
              align="end"
              class="visualization-tabs"
            >
              <b-tab
                v-for="([columnName, correlation]) in correlationsArray"
                :id="columnTabId('correlation-table', columnName)"
                :key="columnName"
                :title="correlation.label"
                :disabled="!isCorrelationEnabled(correlation)"
              >
                <FileColumnMostCommonValuesCorrelationTable
                  :correlations="correlation"
                  @correlation-chosen="changeCorrelationChoice(columnName, $event)"
                />
              </b-tab>
            </b-tabs>
          </b-tab>
          <b-tab
            v-if="hasMapCorrelations"
            title="Correlation Map"
            unmount-lazy
          >
            <GroupVisualizationHeader
              v-bind="headerBindings"
              @title-changed="groupTitleChanged(group, $event)"
              @column-clicked="columnClicked"
              @change-column-order="$emit('changeColumnOrder', group)"
            />
            <b-tabs
              v-model:index="activeColumn"
              pills
              lazy
              align="end"
              class="visualization-tabs"
            >
              <b-tab
                v-for="([columnName, correlation]) in correlationsArray"
                :id="columnTabId('correlation-map', columnName)"
                :key="columnName"
                :title="correlation.label"
                :disabled="!isCorrelationEnabled(correlation)"
              >
                <FileColumnMostCommonValuesCorrelationCountryMap
                  :correlations="correlation"
                  @country-clicked="changeCorrelationChoice(columnName, $event)"
                />
              </b-tab>
            </b-tabs>
          </b-tab>
          <b-tab
            v-if="hasTimelineCorrelations"
            title="Timeline"
            unmount-lazy
          >
            <GroupVisualizationHeader
              v-bind="headerBindings"
              @title-changed="groupTitleChanged(group, $event)"
              @column-clicked="columnClicked"
              @change-column-order="$emit('changeColumnOrder', group)"
            />
            <b-tabs
              v-model:index="activeColumn"
              pills
              lazy
              align="end"
              class="visualization-tabs"
            >
              <b-tab
                v-for="([columnName, correlation]) in timelineCorrelationsArray"
                :id="columnTabId('timeline', columnName)"
                :key="columnName"
                :title="correlation.label"
                :disabled="!isCorrelationEnabled(correlation)"
              >
                <FileColumnMostCommonValuesCorrelationLineChart
                  :correlations="filteredCorrelation(columnName, correlation)"
                />
              </b-tab>
            </b-tabs>
          </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>


<script>

  import Humanize from 'humanize-plus';
  import showdown from 'showdown';
  import FileColumnMostCommonValuesBarChart from './FileColumnMostCommonValuesBarChart.vue';
  import FileColumnMostCommonValuesGroupBarChart from './FileColumnMostCommonValuesGroupBarChart.vue';
  import FileColumnMostCommonValuesPieChart from './FileColumnMostCommonValuesPieChart.vue';
  // import FileColumnMostCommonValuesGroupAreaChart from './FileColumnMostCommonValuesGroupAreaChart.vue';
  import FileColumnMostCommonValuesIpMap from './FileColumnMostCommonValuesIpMap.vue';
  import FileColumnMostCommonValuesCountryMap from './FileColumnMostCommonValuesCountryMap.vue';
  import FileColumnMostCommonValuesHilbert from './FileColumnMostCommonValuesHilbert.vue';
  import FileColumnMostCommonValuesTable from './FileColumnMostCommonValuesTable.vue';
  import FileColumnMostCommonValuesCorrelationTable from './FileColumnMostCommonValuesCorrelationTable.vue';
  import FileColumnMostCommonValuesCorrelationLineChart from './FileColumnMostCommonValuesCorrelationLineChart.vue';
  import FileColumnMostCommonValuesCorrelationCountryMap from './FileColumnMostCommonValuesCorrelationCountryMap.vue';
  import GroupVisualizationHeader from './GroupVisualizationHeader.vue'

  export default {
    components: {
      GroupVisualizationHeader,
      FileColumnMostCommonValuesBarChart,
      FileColumnMostCommonValuesGroupBarChart,
      FileColumnMostCommonValuesPieChart,
      // FileColumnMostCommonValuesGroupAreaChart,
      FileColumnMostCommonValuesCountryMap, FileColumnMostCommonValuesIpMap,
      FileColumnMostCommonValuesHilbert, FileColumnMostCommonValuesTable,
      FileColumnMostCommonValuesCorrelationTable,
      FileColumnMostCommonValuesCorrelationLineChart,
      FileColumnMostCommonValuesCorrelationCountryMap
    },
    props: {
      group: {type: Object, default: null},
      totalRows: {type: Number, default: null}
    },
    emits: ['changeColumnOrder', 'groupTitleChanged'],
    data() {
      const mostCommonValuesChecked = Object.fromEntries(
        this.group.columns.map(
          column => [
            column.name, Array.from(
              new Set(
                this.group.data[column.name].mostCommon.map(
                  mostCommon => Array.from(
                    new Set(
                      mostCommon.correlation.map(
                        correlation => correlation.value
                      )
                    )
                  )
                ).flat()
              )
            )
          ]
        )
      );
      return {
        activeTab: 0,
        activeColumn: 0,
        correlationValue: null,
        mdConverter: new showdown.Converter({tables: true}),
        mostCommonValuesChecked,
        uploadedImages: [],
        draggedOver: false,
        imageError: null,
        attachedImages: [...this.group.attachedImages],
        processingImages: false,
      };
    },
    computed: {
      deployAll: () => ["true", "t", "yes", "y", "on"].includes(
        process.env.VUE_APP_DEPLOY_ALL.toLowerCase()
      ),
      backendUrl: () => process.env.VUE_APP_VISUALIZER_BACKEND_URL,
      backendToken: () => process.env.VUE_APP_BACKEND_TOKEN,
      taskId() { return this.$route.params.taskId; },
      // Bundle everything <GroupVisualizationHeader> needs (data + the image
      // handlers, kept in this parent) so each per-tab instance is a one-liner
      // v-bind. Image state/logic stays here; behaviour is unchanged.
      headerBindings() {
        return {
          group: this.group,
          totalRows: this.totalRows,
          deployAll: this.deployAll,
          definitions: this.columnsWithDefinitions(),
          uploadedImages: this.uploadedImages,
          attachedImages: this.attachedImages,
          draggedOver: this.draggedOver,
          processingImages: this.processingImages,
          imageReference: this.imageReference,
          onDrop: this.onDrop,
          onDragOver: this.onDragOver,
          onDragLeave: this.onDragLeave,
          removeImage: this.removeImage,
          saveImageAttachmentStatus: this.saveImageAttachmentStatus,
        };
      },
      correlationsArray() {
        return Object.entries(this.group.data).map(
          ([columnName, column]) => [
            columnName, {
              label: column.label,
              type: column.correlation.type,
              key: column.correlation.key,
              data: column.mostCommon
            }
          ]
        );
      },
      timelineCorrelationsArray() {
        return this.correlationsArray.filter(
          ([, correlation]) => 'Quarter' === correlation.key
        );
      },
      mapCorrelationsArray() {
        return this.correlationsArray.filter(
          ([, correlation]) => 'country' === correlation.type
        );
      },
      correlations() {
        return Object.fromEntries(this.correlationsArray);
      },
      correlationValues() {
        return Object.fromEntries(
          Object.entries(this.group.data).map(
            ([columnName, column]) => [
              columnName, column.mostCommon.map(
                (correlation, index) => {
                  const total = correlation.correlationTotal;
                  const humanizedTotal = Humanize.compactInteger(total, 1);
                  return {
                    value: index,
                    text: (
                      `${correlation.correlationValue} ` +
                      `(${humanizedTotal} total)`
                    ),
                    key: correlation.correlationValue,
                  };
                }
              )
            ]
          )
        );
      },
      hasCorrelations() {
        return this.correlationsArray.some(
          ([, correlation]) => this.isCorrelationEnabled(correlation)
        );
      },
      hasTimelineCorrelations() {
        return this.timelineCorrelationsArray.some(
          ([, correlation]) => this.isCorrelationEnabled(correlation)
        );
      },
      hasMapCorrelations() {
        return this.mapCorrelationsArray.some(
          ([, correlation]) => this.isCorrelationEnabled(correlation)
        );
      },
      // Ordered list mirroring the outer <b-tab v-if=...> below EXACTLY (order
      // + conditions). Each entry says how many column tabs the viz has and
      // whether a given activeColumn index is selectable there. Drives the
      // auto-skip in the activeTab watcher. Keep in sync with the template.
      activeVisualizations() {
        const columns = this.allColumns();
        const byName = names => index => names.includes((columns[index] || [])[0]);
        const correlations = this.correlationsArray;
        const timeline = this.timelineCorrelationsArray;
        const byCorrelation = array => index => (
          !!array[index] && this.isCorrelationEnabled(array[index][1])
        );
        const list = [{count: columns.length, enabled: () => true}]; // Table
        if (this.nonKeyColumns().length > 0) {
          const enabled = byName(this.nonKeyColumns());
          // multiFeatureBarChart renders one group chart with no column tabs,
          // so nothing to skip there - treat every index as selectable.
          const barEnabled = this.group.features.multiFeatureBarChart ? () => true : enabled;
          list.push({count: columns.length, enabled: barEnabled}); // Bar Chart
          list.push({count: columns.length, enabled}); // Pie Chart
        }
        if (this.mapColumns().length > 0) {
          list.push({count: columns.length, enabled: byName(this.mapColumns())}); // Map
        }
        if (this.hilbertMapColumns().length > 0) {
          list.push({count: columns.length, enabled: byName(this.hilbertMapColumns())}); // Hilbert Map
        }
        if (this.hasCorrelations) {
          list.push({count: correlations.length, enabled: byCorrelation(correlations)}); // Correlation Table
        }
        if (this.hasMapCorrelations) {
          list.push({count: correlations.length, enabled: byCorrelation(correlations)}); // Correlation Map
        }
        if (this.hasTimelineCorrelations) {
          list.push({count: timeline.length, enabled: byCorrelation(timeline)}); // Timeline
        }
        return list;
      },
    },
    watch: {
      // Switching to a visualization where the current column is disabled (e.g.
      // a country-code column under Hilbert Map, which only supports IP columns)
      // would land on a disabled tab showing a blank pane. bvn blocks *clicking*
      // a disabled tab but not *staying* on one across a viz switch, so advance
      // to the next selectable column, wrapping around.
      activeTab() {
        const viz = this.activeVisualizations[this.activeTab];
        if (!viz || viz.count <= 0 || viz.enabled(this.activeColumn)) {
          return;
        }
        for (let offset = 1; offset <= viz.count; offset++) {
          const index = (this.activeColumn + offset) % viz.count;
          if (viz.enabled(index)) {
            this.activeColumn = index;
            return;
          }
        }
      },
    },
    created() {
      this.correlationValue = Object.fromEntries(
        Object.keys(this.group.data).map(
          column => [
            column, this.correlationValues[column].findIndex(
              correlationValue => correlationValue.key === '<all>'
            )
          ]
        )
      );
    },
    async mounted() {
      if (!this.deployAll) {
        return;
      }
      const urlBase = `image/${this.taskId}`;
      const url = `${this.backendUrl}/${urlBase}/?token=${this.backendToken}`;
      const response = await fetch(url, { method: "GET" });
      if (response.ok) {
        this.uploadedImages = (await response.json() || {}).filenames || [];
        // Checkbox state is now data-driven in GroupVisualizationHeader
        // (`:checked="attachedImages.includes(filename)"`), so no imperative
        // ref-poking needed on load.
      }
    },
    methods: {
      onDragOver(event) {
        event.dataTransfer.dropEffect = 'copy';
        this.draggedOver = true;
      },
      onDragLeave() { this.draggedOver = false; },
      async onDrop(event) {
        this.imageError = null;
        this.draggedOver = false;
        this.processingImages = true;
        const files = event.dataTransfer.files;
        if (files.length > 0) {
          const nonImageFiles = [];
          await Promise.all(
            Array.from(files).map(
              file => {
                if (file.type.split('/')[0] === 'image') {
                  this.uploadImage(file.name, file);
                  return;
                }
                nonImageFiles.push(file.name);
              }
            )
          );
          if (nonImageFiles.length > 0) {
            this.imageError = (
              `The following files are not images: ${nonImageFiles.join(', ')}. Only images can be uploaded.`
            );
          }
        }
        this.processingImages = false;
        if (this.imageError) {
          this.$bvModal.show('image-failed');
        }
      },
      async uploadImage(filename, data) {
        this.processingImages = true;
        const urlBase = `image/${this.taskId}/${filename}`;
        const url = `${this.backendUrl}/${urlBase}/?token=${this.backendToken}`;
        const body = new FormData();
        body.append('image', data);
        const response = await fetch(url, { method: "POST", body });
        if (response.ok) {
          this.uploadedImages.push(filename);
        } else {
          const responseData = await response.json();
          if (responseData.errors) {
            this.imageError = `The following error occurred while uploading image ${filename}: ${responseData.errors[0]}`;
          } else {
            this.imageError = `Unknown error occurred while uploading image ${filename}.`;
          }
        }
        // Mark the freshly-uploaded image as attached. Data-driven: no DOM
        // ref — saveImageAttachmentStatus reads `.checked`, and the header's
        // `:checked="attachedImages.includes(filename)"` reflects the result.
        this.saveImageAttachmentStatus(filename, { checked: true });
      },
      async removeImage(filename) {
        this.processingImages = true;
        const urlBase = `image/delete/${this.taskId}/${filename}`;
        const url = `${this.backendUrl}/${urlBase}/?token=${this.backendToken}`;
        const response = await fetch(url, { method: "POST" });
        if (response.ok) {
          this.uploadedImages = this.uploadedImages.filter(
            element => element !== filename
          );
        } else {
          const responseData = await response.json();
          if (responseData.errors) {
            this.imageError = (
              `The following error occurred while removing image ${filename}: ${responseData.errors[0]}`
            );
          } else {
            this.imageError = `Unknown error occurred while removing image ${filename}.`;
          }
          this.$bvModal.show('image-failed');
        }
        this.processingImages = false;
      },
      async saveImageAttachmentStatus(filename, target) {
        this.processingImages = true;
        let urlBase = `${target.checked ? 'at' : 'de'}tach-image/${this.taskId}`;
        const url = `${this.backendUrl}/${urlBase}/?token=${this.backendToken}`;
        const body = JSON.stringify({filename, groupName: this.group.name});
        const response = await fetch(url, { method: "POST", body });
        if (response.ok) {
          if (target.checked) {
            this.attachedImages.push(filename);
          } else {
            this.attachedImages = this.attachedImages.filter(
              element => element !== filename
            );
          }
        } else {
          target.checked = !target.checked;
        }
        this.processingImages = false;
      },
      imageReference(filename) {
        const urlBase = `image/${this.taskId}/${filename}`;
        return `${this.backendUrl}/${urlBase}/?token=${this.backendToken}`;
      },
      groupTitleChanged(group, title) {
        group.title = title;
        this.$emit('groupTitleChanged');
      },
      checkedMostCommonValuesChanged(columnName) {
        return (mostCommonValuesChecked, value) => {
          if (mostCommonValuesChecked.includes(value)) {
            this.mostCommonValuesChecked[columnName].push(value);
            return;
          }
          this.mostCommonValuesChecked[columnName].splice(
            this.mostCommonValuesChecked[columnName].indexOf(value), 1
          );
        };
      },
      filteredCorrelation(column, correlations) {
        const mostCommonValuesChecked = this.mostCommonValuesChecked[column];
        return correlations.data.map(
          correlationsEntry => (
            {
              ...correlationsEntry,
              correlation: correlationsEntry.correlation.filter(
                correlation => mostCommonValuesChecked.includes(
                  correlation.value
                )
              )
            }
          )
        );
      },
      definitionLabel(column) {
        return column.label || this.group.data[column.name].definition.label;
      },
      renderedDefinition(columnName) {
        const definition = this.group.data[columnName].definition;
        const mdCode = definition.description || '';
        return {
          pk: definition.pk,
          label: definition.label,
          description: this.mdConverter.makeHtml(mdCode).replace(
            '<table>',
            '<table class="table table-sm" style="white-space: nowrap; width: 1%">'
          )
        };
      },
      // Explicit stable pane ids for the inner column <b-tab>s. Without them
      // bootstrap-vue-next 0.30.5 generates DIFFERENT ids in BTabs' premount
      // slot scan and at tab registration (registerTab's `?? initialIds`
      // fallback is dead code - BTab's computedId is never nullish), so a
      // <b-tabs> mounted with a preset v-model:index resolves activeId to a
      // premount id no registered tab has: onMounted -> sortTabs finds
      // index -1 and resets the SHARED activeColumn to the first enabled
      // tab, forgetting the selection whenever an unmount-lazy pane
      // remounts. With an explicit :id both sides agree and the preset
      // index survives. Group name keeps ids unique across group tabs open
      // at the same time.
      columnTabId(chart, columnName) {
        const raw = `${this.group.name}-${chart}-${columnName}`;
        return raw.replace(/[^A-Za-z0-9_-]/g, '_');
      },
      columnClicked(...[, clickedColumn]) {
        this.activeColumn = this.allColumns().findIndex(
          column => column[0] === clickedColumn.name
        );
        this.$refs.visualizations.scrollIntoView();
      },
      allColumns() {
        return Object.entries(this.group.data).filter(
          ([, column]) => !column.isHidden
        );
      },
      nonKeyColumns() {
        return this.allColumns().filter(
          ([, column]) => column.type !== 'domain-key'
        ).map(([columnName,]) => columnName);
      },
      columnsWithDefinitions() {
        const result = [];
        this.allColumns().filter(
          ([, column]) => column.definition.description
        ).forEach(
          ([columnName,]) => {
            const rendered = this.renderedDefinition(columnName);
            if (result.map(element => element.pk).includes(rendered.pk)) {
              if (!rendered.label) {
                result.find(
                  element => element.pk === rendered.pk
                ).label += `, ${columnName}`;
              }
              return;
            }
            if (!rendered.label) {
              rendered.label = columnName;
            }
            result.push(rendered);
          }
        );
        return result.map(element => [element.label, element.description]);
      },
      mapColumns() {
        return this.allColumns().filter(
          ([, column]) => ['ip', 'country'].includes(column.type)
        ).map(([columnName,]) => columnName);
      },
      hilbertMapColumns() {
        return this.allColumns().filter(
          ([, column]) => column.type === 'ip'
        ).map(([columnName,]) => columnName);
      },
      mostCommonValuesToShow() {
        const allMostCommonValues = Object.values(this.group.data).map(
          columnEntry => [
            columnEntry.label, columnEntry.mostCommon[0].correlation
          ]
        );
        return Object.fromEntries(allMostCommonValues);
      },
      allMostCommonValuesToShowBy(column) {
        const allMostCommonValues = this.group.data[column].mostCommon;
        const correlationValue = this.correlationValue[column];
        return allMostCommonValues[correlationValue].correlation;
      },
      mostCommonValuesToShowBy(column) {
        const allMostCommonValues = this.group.data[column].mostCommon;
        const correlationValue = this.correlationValue[column];
        const correlation = allMostCommonValues[correlationValue].correlation;
        const mostCommonValuesChecked = this.mostCommonValuesChecked[column];
        return correlation.filter(
          element => mostCommonValuesChecked.includes(element.value)
        );
      },
      totalCount(columnName, column) {
        const allMostCommonValues = this.group.data[columnName].mostCommon;
        const correlationValue = this.correlationValue[columnName];
        const correlation = allMostCommonValues[correlationValue].correlation;
        const mostCommonValuesChecked = this.mostCommonValuesChecked[columnName];
        return column.numValues - correlation.filter(
          element => !mostCommonValuesChecked.includes(element.value)
        ).reduce((accumulator, element) => accumulator + element.count, 0);
      },
      filteredMostCommonValuesToShow() {
        var result = Object.entries(this.mostCommonValuesToShow());
        const showOnly = this.group.features.showOnly;
        if (!showOnly) {
          return result;
        }
        return Object.fromEntries(
          result.map(
            ([label, mostCommon]) => [
              label, mostCommon.filter(entry => showOnly.includes(entry.value))
            ]
          )
        );
      },
      isCorrelationEnabled: correlation => correlation.data.length > 1,
      changeCorrelationChoice(column, correlationValue) {
        this.activeTab = 0;
        const correlationValues = this.correlationValues[column];
        this.correlationValue[column] = correlationValues.findIndex(
          element => element.key === correlationValue
        );
      }
    },
  };
</script>

<style>
  /* The visualization area fills its bounded Report tab-pane (capped at
     ~viewport height in Report.vue) and scrolls INSIDE the outer tab body,
     so the Table/Bar/Pie nav stays put while a long table / chart legend
     scrolls. Flex chain top-down: container -> card -> b-tabs -> tab-content.
     `min-height: 0` at every level is the flexbox scroll gotcha — without it
     a flex item refuses to shrink below its content, so `overflow: auto`
     never actually scrolls and the content spills past its container.
     Target `.visualizations-tabs` DIRECTLY, not `.visualizations-tabs .tabs`:
     bootstrap-vue-next puts that class on the same element as `.tabs`, so the
     old descendant selector missed the tabs root and the chain collapsed the
     root to h=0. The nested per-column `.visualization-tabs` are left in
     normal flow (they scroll with the outer body, no inner scroll). */
  .visualizations-tabs-container,
  .visualizations-tabs-card,
  .visualizations-tabs {
    flex: 1;
    display: flex;
    flex-flow: column;
    min-height: 0;
  }
  .visualizations-tabs > .tab-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    padding-top: 16px;
  }
  /* Per-column pills (.visualization-tabs) sit directly above their content
     with no gap - invisible on white charts/tables, but obvious on the map
     which has a dark background butting straight against the nav. Add a small
     top gap so the content clears the pill row. */
  .visualization-tabs > .tab-content {
    padding-top: 12px;
  }
</style>
